import { db } from './db.ts';

/**
 * Named binary storage. OPFS when it is writable from the main thread (createWritable:
 * Chromium, Firefox, Safari 26+), otherwise Blobs in IndexedDB. Names look like
 * 'blobs/<sha256>' or 'cache/<key>'; OPFS mirrors them as directories.
 */
export interface KeyStore {
  readonly kind: 'opfs' | 'idb' | 'memory';
  get(name: string): Promise<Blob | null>;
  put(name: string, data: Blob): Promise<void>;
  has(name: string): Promise<boolean>;
  delete(name: string): Promise<void>;
  /** Names under a directory prefix, e.g. list('blobs/'). */
  list(prefix: string): Promise<string[]>;
}

export class MemoryKeyStore implements KeyStore {
  readonly kind = 'memory' as const;
  private readonly map = new Map<string, Blob>();
  async get(name: string) {
    return this.map.get(name) ?? null;
  }
  async put(name: string, data: Blob) {
    this.map.set(name, data);
  }
  async has(name: string) {
    return this.map.has(name);
  }
  async delete(name: string) {
    this.map.delete(name);
  }
  async list(prefix: string) {
    return [...this.map.keys()].filter((k) => k.startsWith(prefix));
  }
}

export class IdbKeyStore implements KeyStore {
  readonly kind = 'idb' as const;
  async get(name: string) {
    return (await db().blobs.get(name))?.blob ?? null;
  }
  async put(name: string, data: Blob) {
    await db().blobs.put({ key: name, blob: data });
  }
  async has(name: string) {
    return (await db().blobs.where('key').equals(name).count()) > 0;
  }
  async delete(name: string) {
    await db().blobs.delete(name);
  }
  async list(prefix: string) {
    return (await db().blobs.where('key').startsWith(prefix).primaryKeys()) as string[];
  }
}

export class OpfsKeyStore implements KeyStore {
  readonly kind = 'opfs' as const;
  constructor(private readonly root: FileSystemDirectoryHandle) {}

  private async dirFor(name: string, create: boolean): Promise<[FileSystemDirectoryHandle, string] | null> {
    const parts = name.split('/');
    const file = parts.pop()!;
    let dir = this.root;
    try {
      for (const p of parts) dir = await dir.getDirectoryHandle(p, { create });
    } catch {
      return null;
    }
    return [dir, file];
  }

  async get(name: string) {
    const loc = await this.dirFor(name, false);
    if (!loc) return null;
    try {
      const handle = await loc[0].getFileHandle(loc[1]);
      return await handle.getFile();
    } catch {
      return null;
    }
  }

  async put(name: string, data: Blob) {
    const loc = await this.dirFor(name, true);
    if (!loc) throw new Error(`Could not store ${name}.`);
    // createWritable writes to a swap file and commits atomically on close().
    const handle = await loc[0].getFileHandle(loc[1], { create: true });
    const w = await handle.createWritable();
    await w.write(data);
    await w.close();
  }

  async has(name: string) {
    const loc = await this.dirFor(name, false);
    if (!loc) return false;
    try {
      await loc[0].getFileHandle(loc[1]);
      return true;
    } catch {
      return false;
    }
  }

  async delete(name: string) {
    const loc = await this.dirFor(name, false);
    if (!loc) return;
    try {
      await loc[0].removeEntry(loc[1]);
    } catch {
      // already gone
    }
  }

  async list(prefix: string) {
    const dirName = prefix.replace(/\/$/, '');
    const loc = await this.dirFor(`${dirName}/x`, false);
    if (!loc) return [];
    const out: string[] = [];
    const dir = loc[0] as FileSystemDirectoryHandle & { keys(): AsyncIterable<string> };
    for await (const name of dir.keys()) if (!name.endsWith('.part')) out.push(`${dirName}/${name}`);
    return out;
  }
}

/** Picks OPFS when a probe write succeeds, else IndexedDB. */
export async function openKeyStore(): Promise<KeyStore> {
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      const probe = await root.getFileHandle('.probe', { create: true });
      if (typeof (probe as FileSystemFileHandle & { createWritable?: unknown }).createWritable === 'function') {
        const w = await probe.createWritable();
        await w.write(new Blob(['ok']));
        await w.close();
        await root.removeEntry('.probe');
        return new OpfsKeyStore(root);
      }
    }
  } catch {
    // fall through to IndexedDB
  }
  return new IdbKeyStore();
}
